/**
 * Backend Analytics Provider
 * Formats and sends service-selected system events to backend Elasticsearch.
 */

import { BaseAnalyticsProvider } from './base.provider'
import { backendAnalyticsService } from '../services/backend-analytics.service'
import type { AnalyticsEventProperties } from '../types/properties'

interface BackendPayload {
  eventName: string
  locationId: string
  contactId: string
  groupId?: string
  eventData: Record<string, unknown>
}

interface BackendAnalyticsLogger {
  debug?: (message: string, context?: Record<string, unknown>) => void
  error: (message: string, context?: Record<string, unknown>) => void
}

export class BackendAnalyticsProvider extends BaseAnalyticsProvider<BackendPayload> {
  readonly name = 'BackendAnalytics'
  private debug = false
  private logger: BackendAnalyticsLogger | null = null

  constructor(debug = false, logger?: BackendAnalyticsLogger) {
    super()
    this.debug = debug
    this.logger = logger ?? null
  }

  transform(event: string, properties: AnalyticsEventProperties): BackendPayload | null {
    const backendEventName = event.trim()
    if (!backendEventName) return null

    return {
      eventName: backendEventName,
      locationId: properties.locationId || 'NA',
      contactId: properties.contactId || 'NA',
      groupId: properties.groupId,
      eventData: {
        ...properties,
        platform: properties.platform || 'web',
      },
    }
  }

  send(payload: BackendPayload): void {
    try {
      backendAnalyticsService.trackEvent(payload)
      if (this.debug) this.logger?.debug?.('[BackendAnalytics] Event tracked', { eventName: payload.eventName })
    } catch (error) {
      this.logger?.error('[BackendAnalytics] Event delivery failed', { error })
    }
  }
}
